import invariant from 'invariant';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { sprintf } from 'sprintf-js';
import { styled } from 'styled-components';

import type { ApiFileUploadTypes } from 'api/types/ApiFileUploadTypes';
import { Box } from 'components/Box/Box';
import { FieldError } from 'components/Form/Field/FieldError';
import { configValue } from 'config/appConfigUtils';
import { HttpError } from 'utils/HttpError';
import { getUserType } from 'utils/analytics/helpers/getUserType';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import type { BackgroundTask } from 'utils/asyncBackendTask/asyncBackendTaskTypes';
import { waitTaskCompleted } from 'utils/asyncBackendTask/waitTaskCompleted';
import { getJson } from 'utils/http/getJson';
import { postData } from 'utils/http/postData';
import { postJson } from 'utils/http/postJson';
import { fileIdToUrl } from 'utils/s3file';
import { useUserEnvironment } from 'zustand-stores/userEnvironmentStore';

type S3Upload = {
  params: {
    fields: Record<string, string>;
    url: string;
  };
  s3File: {
    id: string;
  };
};

const InputContainer = styled(Box)`
  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;

    &[disabled] {
      cursor: not-allowed;
    }
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const objToFormData = (o: Record<string, any>): FormData => {
  const f = new FormData();
  Object.entries(o).forEach(([k, v]) => f.append(k, v));
  return f;
};

const uploadPresignedUrl = async (f: File, r: S3Upload) => {
  const { params, s3File } = r;
  const formData = objToFormData(params.fields);
  formData.append('file', f);

  return postData(params.url, formData).then(() =>
    postJson<{ task: BackgroundTask<unknown> }>(
      `/data/s3-uploads/${s3File.id}/done`,
    ).then(({ task }) => waitTaskCompleted(task).then(() => s3File.id)),
  );
};

const useMock = configValue('idealist', 'fileUploadMock');

const uploadToS3 = (f: File, fileType: ApiFileUploadTypes) =>
  postJson<S3Upload>('/data/s3-uploads', {
    fileType,
    filename: f.name,
    size: f.size,
    // bytes
    mimeType: f.type,
  }).then((response) => uploadPresignedUrl(f, response));

type Props = {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: (arg0: Record<string, any>) => ReactNode;
  downloadAttachment?: boolean;
  name: string;
  isRequired: boolean;
  qaId: string;
  fileId: string | undefined;
  fileType: ApiFileUploadTypes;
  onChange: (s3FileId: string) => void;
  onError?: (arg0: Error) => void;
  accept?: string;
  'aria-label'?: string;
  disabled?: boolean;
  extraAnalyticsData?: Record<string, unknown>;
  maxSize?: number; // in MB
};

export function S3Picker({
  children,
  name,
  downloadAttachment,
  isRequired,
  qaId,
  fileId,
  fileType,
  onChange,
  onError,
  accept,
  'aria-label': ariaLabel,
  disabled,
  extraAnalyticsData,
  maxSize,
}: Props) {
  const { user } = useUserEnvironment();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [urlPreview, setUrlPreview] = useState(null);
  const [filename, setFilename] = useState(null);
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [dragActive, setDragActive] = useState(false);
  const [mouseActive, setMouseActive] = useState(false);

  const onMouseEnter = () => {
    setMouseActive(true);
  };

  const onMouseLeave = () => {
    setMouseActive(false);
  };

  const onDragEnter = () => {
    setDragActive(true);
    setMouseActive(true);
  };

  const onDragEnd = () => {
    setDragActive(false);
    setMouseActive(false);
  };

  const onClick = () => {
    if (!useMock && inputRef.current) {
      inputRef.current.click();
    }
  };

  const onFileChange = useCallback(() => {
    const fs = inputRef.current?.files;

    if (!fs || fs.length === 0) return;

    invariant(fs, 'fs must be defined'); // for flow

    const f = fs[0];
    if (maxSize && f.size > maxSize * 1000000) {
      setError(
        sprintf(getText('Maximum file size: %(maxSize)s MB'), { maxSize }),
      );
      return;
    }

    setLoading(true);
    uploadToS3(f, fileType)
      .then((newFileId) => {
        // Track non-staff S3 uploads
        if (fileType !== 'CMS_UPLOAD') {
          let fileExtension;

          // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
          if (f && f.name) {
            fileExtension = f.name.split('.').pop();
          }

          trackEvent('Success: Uploaded File', {
            file_type: fileType,
            file_extension: fileExtension,
            user_type: getUserType(user),
            ...extraAnalyticsData,
          });
        }

        // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        setFilename(f.name);
        // @ts-expect-error TS(2345): Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
        setCreated(new Date());
        // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        setUrlPreview(URL.createObjectURL(f));
        onChange(newFileId);
      })
      .catch((e) => {
        if (
          e instanceof HttpError &&
          e.responseJson?.errors.some(
            (err) => err.name === 'mimeType' && err.description === 'Required',
          )
        ) {
          setError(getText('Unsupported file format'));
        } else if (onError) onError(e);
      })
      .finally(() => {
        setLoading(false);
        setDragActive(false);
        setMouseActive(false);
      });
  }, [maxSize, fileType, onChange, user, extraAnalyticsData, onError]);

  useEffect(() => {
    if (fileId && !filename) {
      setLoading(true);
      getJson(`/data/s3-uploads/${fileId}/meta`)
        // @ts-expect-error TS(2345): Argument of type '({ s3File }: { s3File: any; }) =... Remove this comment to see the full error message
        .then(({ s3File }) => {
          setFilename(s3File.filename);
          setCreated(s3File.created);
        })
        .finally(() => {
          setError(undefined);
          setLoading(false);
        });
    }

    if (fileId && !urlPreview) {
      // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
      setUrlPreview(fileIdToUrl(fileId, downloadAttachment));
    }
  }, [fileId, urlPreview, filename, downloadAttachment]);

  useEffect(() => {
    const input = inputRef.current;

    // If the file was removed outside the S3Picker, clear the input value
    if (!fileId && input) input.value = '';
  }, [fileId]);

  return (
    <>
      <InputContainer data-qa-id={qaId}>
        <input
          aria-label={ariaLabel}
          ref={inputRef}
          required={isRequired}
          name={name}
          type="file"
          onChange={onFileChange}
          disabled={disabled}
          data-qa-id={`${qaId}-hidden-input`}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onDragEnter={onDragEnter}
          onDragEnd={onDragEnd}
          onDragLeave={onDragEnd}
          onFocus={onMouseEnter}
          onBlur={onMouseLeave}
          accept={accept}
        />
        {children({
          onClick,
          urlPreview,
          filename,
          loading,
          dragActive: !disabled && dragActive,
          mouseActive: !disabled && mouseActive,
          created,
        })}
      </InputContainer>
      {error && <FieldError errorMessage={error} />}
    </>
  );
}
